import * as THREE from 'three';
import { params } from '../config.js';
import * as SceneModule from '../scene.js';
import {
  airplaneState,
  keysPressed,
  mouseControl,
  gyroControl,
  touchControl,
  getAirplaneMaterials
} from './state.js';
import { getAirplaneGroup } from './create.js';
import { state, scrollOffset } from '../terrain/tiling.js';
import { sampleTerrainHeight } from '../terrain/heightmap.js';
import { updateTiltIndicator, updateGyroStatus } from '../controls/gyro.js';
import { pointMaterial } from '../terrain/material.js';

// Reusable objects for update loop (avoid GC pressure)
const _forward = new THREE.Vector3();
const _right = new THREE.Vector3();
const _up = new THREE.Vector3(0, 1, 0);
const _camForward = new THREE.Vector3();
const _camRight = new THREE.Vector3();
const _lookTarget = new THREE.Vector3();

// Reusable colors (avoid creating every frame)
const _dangerColor = new THREE.Color(0xff0000);   // red when colliding
const _safeColor = new THREE.Color(0xff9000);     // yellow when safe (matches HUD)
const _landedColor = new THREE.Color(0x4ade80);   // green when landed

// Launch function - take off from landed state
export function launchAirplane() {
  if (airplaneState.flightState !== 'landed') return;

  // Hide landed overlay
  const landedOverlay = document.getElementById('landed-overlay');
  if (landedOverlay) landedOverlay.classList.remove('visible');

  // Gentle lift off - just enough to clear terrain, then user has control
  // Small target rise, smoothing will handle the gradual movement
  airplaneState.targetY = airplaneState.y + 0.4; // Small gentle rise

  // Clear any residual collision state
  airplaneState.collisionShake = 0;
  airplaneState.isColliding = false;

  // Start with zero speed - user controls throttle with W
  state.scrollSpeed = 0;
  params.scrollSpeed = 0;
  
  // Reset flight state - user now has full control
  airplaneState.flightState = 'flying';
}

// Restart function - reset after crash
export function restartAirplane() {
  if (airplaneState.flightState !== 'crashed') return;

  // Hide crash overlay
  const crashOverlay = document.getElementById('crash-overlay');
  if (crashOverlay) crashOverlay.classList.remove('visible');

  // Reset airplane position and state
  airplaneState.heading = 0;
  airplaneState.targetHeading = 0;
  airplaneState.y = 0;
  airplaneState.targetY = 0;
  airplaneState.tilt = 0;
  airplaneState.pitch = 0;
  airplaneState.strafeOffset = 0;
  airplaneState.targetStrafeOffset = 0;
  airplaneState.collisionShake = 0;
  airplaneState.worldPos.set(0, airplaneState.baseAltitude, 0);

  // Reset terrain scroll to move to a "new" area
  scrollOffset.x = 0;
  scrollOffset.y = 0;

  // Set initial speed
  state.scrollSpeed = 3.0;
  params.scrollSpeed = 3.0;
  
  // Reset flight state
  airplaneState.flightState = 'flying';
}

export function updateAirplane(delta) {
  const { camera } = SceneModule;
  const airplaneGroup = getAirplaneGroup();
  if (!airplaneGroup || !airplaneState.enabled) return;

  const isChaseMode = params.cameraMode === 'chase';
  const isTopdownMode = params.cameraMode === 'topdown';
  const isIsoMode = params.cameraMode === 'isometric';

  // ========== PROCESS GYRO INPUT ==========
  if (gyroControl.enabled && gyroControl.active) {
    // Handle manual calibration request (recalibrate button)
    if (gyroControl.needsCalibration) {
      gyroControl.neutralBeta = gyroControl.rawBeta;
      gyroControl.neutralGamma = gyroControl.rawGamma;
      gyroControl.calibrated = true;
      gyroControl.needsCalibration = false;
      console.log('Gyro manually calibrated:', { beta: gyroControl.neutralBeta, gamma: gyroControl.neutralGamma });
    }

    // Calculate delta from neutral position
    let deltaBeta = gyroControl.rawBeta - gyroControl.neutralBeta;   // pitch (climb/dive)
    let deltaGamma = gyroControl.rawGamma - gyroControl.neutralGamma; // roll (turn)
    
    // Apply dead zone (smaller for more responsiveness)
    const deadZone = 2; // degrees
    if (Math.abs(deltaBeta) < deadZone) deltaBeta = 0;
    if (Math.abs(deltaGamma) < deadZone) deltaGamma = 0;

    // Normalize to -1 to 1 range
    // gamma: positive = tilted right, so positive deltaGamma = turn right
    // beta: positive = tilted forward, so positive deltaBeta = nose down (dive)
    const maxAngle = 25; // degrees for full deflection (reduced for sensitivity)
    const rawTurnRate = Math.max(-1, Math.min(1, deltaGamma / maxAngle));
    const rawClimbRate = Math.max(-1, Math.min(1, deltaBeta / maxAngle)); // positive: tilt forward = climb

    // Smooth the input (higher value = more responsive)
    const smoothFactor = 0.3;
    gyroControl.smoothTurnRate += (rawTurnRate - gyroControl.smoothTurnRate) * smoothFactor;
    gyroControl.smoothClimbRate += (rawClimbRate - gyroControl.smoothClimbRate) * smoothFactor;

    gyroControl.turnRate = gyroControl.smoothTurnRate;
    gyroControl.climbRate = gyroControl.smoothClimbRate;

    // Update tilt indicator visual
    updateTiltIndicator(gyroControl.turnRate, gyroControl.climbRate);

    // Debug: update status with control values
    const t = gyroControl.turnRate.toFixed(2);
    const c = gyroControl.climbRate.toFixed(2);
    updateGyroStatus(true, `T:${t} C:${c}`);
  }

  // ========== HANDLE THROTTLE INPUT ==========
  // Keyboard (W/S) or Touch zones control speed (only when flying)
  const throttleSpeed = 3.0; // speed change per second
  const maxSpeed = 10.0;
  const minSpeed = 0.0;

  if (airplaneState.flightState === 'flying') {
    // Throttle up: keyboard W or touch zone
    // Don't cap at maxSpeed if currently boosting (boost allows going higher)
    if (keysPressed.throttleUp || touchControl.throttleUp) {
      const speedCap = airplaneState.isBoosting ? (maxSpeed + 5.0) : maxSpeed;
      state.scrollSpeed = Math.min(speedCap, state.scrollSpeed + throttleSpeed * delta);
      params.scrollSpeed = state.scrollSpeed;
    }
    // Throttle down: keyboard S or touch zone
    if (keysPressed.throttleDown || touchControl.throttleDown) {
      state.scrollSpeed = Math.max(minSpeed, state.scrollSpeed - throttleSpeed * delta);
      params.scrollSpeed = state.scrollSpeed;
    }

    // ========== BOOST SYSTEM LOGIC ==========
    const boostChargeSpeed = 0.5; // Charges in 2 seconds
    const boostDuration = 3.0;
    const boostMaxSpeed = maxSpeed + 5.0; // Boost to 15.0

    if (airplaneState.isShiftPressed && !airplaneState.isBoosting && airplaneState.boostCharge < 1.0) {
      airplaneState.boostCharge = Math.min(1.0, airplaneState.boostCharge + boostChargeSpeed * delta);
      if (airplaneState.boostCharge >= 1.0) {
        airplaneState.isBoosting = true;
        airplaneState.boostTimeRemaining = boostDuration;
        airplaneState.boostCharge = 0; // Reset charge after activation
      }
    } else if (!airplaneState.isShiftPressed && !airplaneState.isBoosting) {
      // Decay charge if shift is released early
      airplaneState.boostCharge = Math.max(0, airplaneState.boostCharge - boostChargeSpeed * delta);
    }

    if (airplaneState.isBoosting) {
      airplaneState.boostTimeRemaining -= delta;

      // Smoothly ramp to boost speed
      const rampSpeed = throttleSpeed * 4; // Faster ramp for boost
      state.scrollSpeed = Math.min(boostMaxSpeed, state.scrollSpeed + rampSpeed * delta);
      params.scrollSpeed = state.scrollSpeed;

      if (airplaneState.boostTimeRemaining <= 0) {
        airplaneState.isBoosting = false;
        airplaneState.boostTimeRemaining = 0;
      }
    } else if (state.scrollSpeed > maxSpeed) {
      // Ease back to normal max speed after boost
      state.scrollSpeed = Math.max(maxSpeed, state.scrollSpeed - throttleSpeed * 2 * delta);
      params.scrollSpeed = state.scrollSpeed;
    }

    // Dynamic FOV and Mask Radius based on speed
    const speedFactor = state.scrollSpeed / maxSpeed;
    
    // Smooth the FOV factor for a "pressed into seat" feeling
    // FOV lags behind actual speed changes
    const fovDiff = speedFactor - airplaneState.smoothFovFactor;
    airplaneState.smoothFovFactor += fovDiff * Math.min(1, params.fovEasing * delta);

    // Update FOV using smoothed factor
    const targetFov = params.minFov + (params.maxFov - params.minFov) * airplaneState.smoothFovFactor;
    camera.fov = targetFov;
    camera.updateProjectionMatrix();
    params.camFov = targetFov;

    // Update Mask Radius (can use direct speed factor for more immediate feedback)
    const targetMaskRadius = params.minMaskRadius + (params.maxMaskRadius - params.minMaskRadius) * speedFactor;
    params.maskRadius = targetMaskRadius;
    pointMaterial.uniforms.uMaskRadius.value = targetMaskRadius;

    // Handle strafe input - A/D or touch zones control lateral movement
    if (keysPressed.strafeLeft || touchControl.strafeLeft) {
      airplaneState.targetStrafeOffset = Math.max(-airplaneState.maxStrafe,
        airplaneState.targetStrafeOffset - airplaneState.strafeSpeed * delta);
    }
    if (keysPressed.strafeRight || touchControl.strafeRight) {
      airplaneState.targetStrafeOffset = Math.min(airplaneState.maxStrafe,
        airplaneState.targetStrafeOffset + airplaneState.strafeSpeed * delta);
    }
  }
  // Ease strafe offset toward target (heavy ease-in)
  const strafeDiff = airplaneState.targetStrafeOffset - airplaneState.strafeOffset;
  airplaneState.strafeOffset += strafeDiff * Math.min(1, airplaneState.strafeEasing * delta);

  // ========== HANDLE STEERING INPUT ==========
  // Determine active turn/climb rates from mouse OR gyro
  let activeTurnRate = 0;
  let activeClimbRate = 0;
  let hasSteeringInput = false;

  // Gyro takes priority on mobile if active
  if (gyroControl.enabled && gyroControl.active && airplaneState.flightState === 'flying') {
    activeTurnRate = gyroControl.turnRate;
    activeClimbRate = gyroControl.climbRate;
    hasSteeringInput = true;
  }
  // Fall back to mouse on desktop
  else if (mouseControl.active && airplaneState.flightState === 'flying') {
    activeTurnRate = mouseControl.turnRate;
    activeClimbRate = mouseControl.climbRate;
    hasSteeringInput = true;
  }

  // Apply steering if we have input
  if (hasSteeringInput) {
    // Turn rate: position determines how fast we turn
    const baseTurnSpeed = 2.0; // radians per second at full deflection (increased)
    const turnSpeed = state.scrollSpeed > 0 ? baseTurnSpeed : baseTurnSpeed * 0.5;
    airplaneState.heading += activeTurnRate * turnSpeed * delta;

    // Climb rate: controls altitude change
    const baseClimbSpeed = 4.0; // units per second at full deflection (increased)
    const climbSpeed = state.scrollSpeed > 0 ? baseClimbSpeed : baseClimbSpeed * 0.5;
    airplaneState.targetY += activeClimbRate * climbSpeed * delta;
    airplaneState.targetY = Math.max(-airplaneState.altitudeRange,
      Math.min(airplaneState.altitudeRange, airplaneState.targetY));
  }

  // Smooth vertical movement toward target
  const dy = airplaneState.targetY - airplaneState.y;
  airplaneState.y += dy * Math.min(1, airplaneState.smoothing * delta);

  // Calculate tilt based on turning (bank into turns like real airplane)
  const tiltAmount = state.scrollSpeed > 0 ? 0.5 : 0.15;
  const targetTilt = activeTurnRate * tiltAmount;
  airplaneState.tilt += (targetTilt - airplaneState.tilt) * Math.min(1, 6 * delta);

  // Calculate pitch based on vertical movement
  const targetPitch = dy * 0.8; // nose up when climbing, down when diving
  airplaneState.pitch += (targetPitch - airplaneState.pitch) * Math.min(1, 6 * delta);

  // Get forward direction based on heading (heading 0 = -Z direction)
  _forward.set(
    Math.sin(airplaneState.heading),
    0,
    -Math.cos(airplaneState.heading)
  );

  // Get right vector
  _right.crossVectors(_forward, _up).normalize();
  
  if (isChaseMode || isTopdownMode || isIsoMode) {
    // Free roam mode: airplane stays at origin, faces heading direction
    // Terrain scrolls based on heading and speed
    // Strafe offset is perpendicular to heading
    const strafeX = Math.cos(airplaneState.heading) * airplaneState.strafeOffset;
    const strafeZ = Math.sin(airplaneState.heading) * airplaneState.strafeOffset;

    airplaneState.worldPos.x = strafeX;
    airplaneState.worldPos.y = airplaneState.baseAltitude + airplaneState.y;
    airplaneState.worldPos.z = strafeZ;

    // Set airplane position from world pos
    airplaneGroup.position.copy(airplaneState.worldPos);
  } else {
    // Normal mode: airplane positioned relative to camera
    camera.getWorldDirection(_camForward);
    _camForward.y = 0;
    if (_camForward.lengthSq() < 1e-6) _camForward.set(0, 0, -1);
    _camForward.normalize();
    
    _camRight.crossVectors(_camForward, _up).normalize();
    
    airplaneGroup.position.copy(camera.position);
    airplaneGroup.position.addScaledVector(_camForward, airplaneState.distance);
    airplaneGroup.position.y = camera.position.y + airplaneState.height + airplaneState.y;
  }

  // Update scale
  airplaneGroup.scale.set(airplaneState.size, airplaneState.size, airplaneState.size);

  // Orient airplane to face heading direction with tilt and pitch
  _lookTarget.copy(airplaneGroup.position).add(_forward);
  airplaneGroup.lookAt(_lookTarget);
  airplaneGroup.rotation.z = airplaneState.tilt;
  airplaneGroup.rotation.x += airplaneState.pitch;

  const time = performance.now() * 0.001;

  // Collision detection - sample actual terrain height at airplane position
  const terrainHeight = sampleTerrainHeight(airplaneGroup.position.x, airplaneGroup.position.z);

  const wasColliding = airplaneState.isColliding;
  if (terrainHeight !== null) {
    // Compare airplane Y to actual terrain height at this position
    const clearance = airplaneGroup.position.y - terrainHeight;
    airplaneState.isColliding = clearance < airplaneState.collisionThreshold;
  } else {
    airplaneState.isColliding = false; // No heightmap yet
  }

  // Handle collision effects
  // Airplane uses fixed UI colors (yellow/red), separate from ground highlight
  
  // Get UI overlay elements
  const landedOverlay = document.getElementById('landed-overlay');
  const crashOverlay = document.getElementById('crash-overlay');

  // Get airplane materials
  const airplaneMaterials = getAirplaneMaterials();

  // Only process new collisions when in flying state
  if (airplaneState.isColliding && airplaneState.flightState === 'flying') {
    // Determine if this is a crash or landing based on speed
    if (state.scrollSpeed <= airplaneState.crashSpeedThreshold) {
      // Landing - low/no speed
      airplaneState.flightState = 'landed';
      state.scrollSpeed = 0;
      params.scrollSpeed = 0;
      
      // Show landed overlay
      if (landedOverlay) landedOverlay.classList.add('visible');
      if (crashOverlay) crashOverlay.classList.remove('visible');

      // Turn airplane to landed color (green)
      for (const mat of airplaneMaterials) {
        mat.color.copy(_landedColor);
      }
    } else {
      // Crash - too fast!
      airplaneState.flightState = 'crashed';
      state.scrollSpeed = 0;
      params.scrollSpeed = 0;
      
      // Show crash overlay
      if (crashOverlay) crashOverlay.classList.add('visible');
      if (landedOverlay) landedOverlay.classList.remove('visible');
    }
  }

  // Handle visual effects based on flight state
  if (airplaneState.flightState === 'crashed') {
    // Increase shake intensity for crash
    airplaneState.collisionShake = Math.min(airplaneState.collisionShake + delta * 15, 1.0);

    // Turn airplane to danger color
    for (const mat of airplaneMaterials) {
      mat.color.copy(_dangerColor);
    }

    // Apply intense shake
    const shakeIntensity = airplaneState.collisionShake * 0.08;
    airplaneGroup.position.x += (Math.random() - 0.5) * shakeIntensity;
    airplaneGroup.position.y += (Math.random() - 0.5) * shakeIntensity;
    airplaneGroup.position.z += (Math.random() - 0.5) * shakeIntensity;
    airplaneGroup.rotation.z += (Math.random() - 0.5) * shakeIntensity * 0.5;
  } else if (airplaneState.flightState === 'landed') {
    // Landed state - airplane stays still, green color
    airplaneState.collisionShake = 0;
    for (const mat of airplaneMaterials) {
      mat.color.copy(_landedColor);
    }
  } else if (airplaneState.isColliding) {
    // Flying but scraping terrain - warning shake
    airplaneState.collisionShake = Math.min(airplaneState.collisionShake + delta * 15, 1.0);

    // Turn airplane to danger color
    for (const mat of airplaneMaterials) {
      mat.color.copy(_dangerColor);
    }

    // Apply shake
    const shakeIntensity = airplaneState.collisionShake * 0.08;
    airplaneGroup.position.x += (Math.random() - 0.5) * shakeIntensity;
    airplaneGroup.position.y += (Math.random() - 0.5) * shakeIntensity;
    airplaneGroup.position.z += (Math.random() - 0.5) * shakeIntensity;
    airplaneGroup.rotation.z += (Math.random() - 0.5) * shakeIntensity * 0.5;
  } else {
    // Decay shake when not colliding
    airplaneState.collisionShake = Math.max(airplaneState.collisionShake - delta * 5, 0);

    // Transition to safe color smoothly
    for (const mat of airplaneMaterials) {
      const currentColor = mat.color;
      currentColor.r += (_safeColor.r - currentColor.r) * delta * 5;
      currentColor.g += (_safeColor.g - currentColor.g) * delta * 5;
      currentColor.b += (_safeColor.b - currentColor.b) * delta * 5;
    }

    // Add subtle bobbing motion only when not colliding
    airplaneGroup.position.y += Math.sin(time * 2) * 0.02;
  }
}

// Setup crash overlay click handler for mobile
export function setupCrashOverlayHandler() {
  const crashOverlayEl = document.getElementById('crash-overlay');
  if (crashOverlayEl) {
    crashOverlayEl.addEventListener('click', () => {
      if (airplaneState.flightState === 'crashed') {
        restartAirplane();
      }
    });
  }
}
